import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, FileText, ExternalLink, Link2, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const ComprehensiveFAQ = () => {
  const [openCategories, setOpenCategories] = useState(new Set());
  const [openItems, setOpenItems] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);

  // Generate URL-friendly anchor from question text
  const generateAnchor = (categoryIndex, itemIndex, question) => {
    const cleanText = question
      .toLowerCase()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return `cat-${categoryIndex}-q-${itemIndex}-${cleanText}`;
  };


  // Copy link to clipboard with fallback
  const copyLink = async (anchor) => {
    const url = `${window.location.origin}${window.location.pathname}#${anchor}`;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        setCopiedId(anchor);
        setTimeout(() => setCopiedId(null), 2000);
        return;
      }
    } catch (err) {
      console.warn('Clipboard API failed, using fallback:', err);
    }

    // Fallback method for older browsers or non-HTTPS
    try {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopiedId(anchor);
        setTimeout(() => setCopiedId(null), 2000);
      } else {
        throw new Error('execCommand failed');
      }
    } catch (fallbackErr) {
      console.error('All copy methods failed:', fallbackErr);
      // Still show feedback even if copy failed
      setCopiedId(anchor);
      setTimeout(() => setCopiedId(null), 1000);
    }
  };

  // Handle initial page load and anchor navigation
  useEffect(() => {

    // Handle initial page load with anchor
    const hash = window.location.hash.replace('#', '');
    if (hash.startsWith('cat-')) {
      const parts = hash.split('-');
      if (parts.length >= 4) {
        const categoryIndex = parseInt(parts[1]);
        const itemIndex = parseInt(parts[3]);
        
        // Open the category and item
        setOpenCategories(new Set([categoryIndex]));
        setOpenItems(new Set([`${categoryIndex}-${itemIndex}`]));
        
        // Allow time for accordion to open, then scroll to element
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            const questionHeader = element.querySelector('[data-question-header]');
            if (questionHeader) {
              // Scroll to question header with offset
              const headerRect = questionHeader.getBoundingClientRect();
              const scrollTop = window.pageYOffset + headerRect.top - 80;
              
              window.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
              });
            } else {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }, 300);
      }
    }
  }, []);


  // Handle hash changes (back/forward navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash.startsWith('cat-')) {
        const parts = hash.split('-');
        if (parts.length >= 4) {
          const categoryIndex = parseInt(parts[1]);
          const itemIndex = parseInt(parts[3]);
          
          setOpenCategories(new Set([categoryIndex]));
          setOpenItems(new Set([`${categoryIndex}-${itemIndex}`]));
          
          setTimeout(() => {
            const element = document.getElementById(hash);
            if (element) {
              const questionHeader = element.querySelector('[data-question-header]');
              if (questionHeader) {
                const headerRect = questionHeader.getBoundingClientRect();
                const scrollTop = window.pageYOffset + headerRect.top - 80;
                
                window.scrollTo({
                  top: scrollTop,
                  behavior: 'smooth'
                });
              } else {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }
          }, 300);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const toggleCategory = (categoryIndex) => {
    const newOpenCategories = new Set(openCategories);
    if (newOpenCategories.has(categoryIndex)) {
      newOpenCategories.delete(categoryIndex);
    } else {
      newOpenCategories.add(categoryIndex);
    }
    setOpenCategories(newOpenCategories);
  };

  const toggleItem = (itemKey) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(itemKey)) {
      newOpenItems.delete(itemKey);
    } else {
      newOpenItems.add(itemKey);
    }
    setOpenItems(newOpenItems);
  };

  const PDFLink = ({ fileName, children }) => (
    <a 
      href={`/files/ffaaa/note-sante/${fileName}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-decoration-none transition-all duration-300 hover:opacity-80"
      style={{ color: 'var(--brand-primary)' }}
    >
      <FileText size={16} />
      {children}
      <ExternalLink size={14} />
    </a>
  );

  const faqCategories = [
    {
      title: "DÉBUTER L'AÏKIDO",
      items: [
        {
          question: "Puis-je essayer avant de m'engager ?",
          answer: "Oui, nous proposons deux cours d'essai gratuits sans obligation. Venez en tenue de sport souple et longue (évitez les shorts pour les frottements de la peau sur les tatamis), aucun équipement n'est requis. Vous décidez ensuite si l'aïkido vous convient."
        },
        {
          question: "Suis-je trop âgé(e) ou pas assez en forme pour commencer ?",
          answer: "L'aïkido convient à tous les âges et niveaux de forme physique. La pratique est basée sur la fluidité et la technique plutôt que la force brute. Nous accueillons régulièrement des débutants de 40, 50 ans et plus. Votre condition physique se développera progressivement avec la pratique."
        },
        {
          question: "Je ne suis pas souple - puis-je quand même pratiquer ?",
          answer: "La souplesse se développe avec l'entraînement. La pratique des mouvements de l'aïkido améliorera peu à peu la flexibilité."
        },
        {
          question: "À quoi dois-je m'attendre lors de mon premier cours ?",
          answer: "Mouvements de base et techniques simples. Vous serez guidé par des pratiquants expérimentés et bienveillants. Nous commençons par la latéralisation (différencier gauche/droite) et les chutes de base."
        },
        {
          question: "Vais-je m'intégrer facilement ? Je n'ai jamais fait d'arts martiaux.",
          answer: "Notre dojo accueille spécifiquement les débutants. Chacun de nos gradés a commencé exactement là où vous êtes maintenant. Nous privilégions une communauté bienveillante où chacun aide les autres à progresser. (Et nous nous retrouvons au bar avant chaque vacances scolaires !)"
        }
      ]
    },
    {
      title: "COMPRENDRE L'AÏKIDO",
      items: [
        {
          question: "Qu'est-ce que l'aïkido exactement ?",
          answer: "L'aïkido est un art martial défensif qui enseigne à aborder un conflit avec un état d'esprit constructif plutôt que destructif. Il développe l'appréciation du timing, de la distance et l'utilisation de la force du partenaire pour réaliser des projections et des immobilisations."
        },
        {
          question: "Quelle est la différence avec les autres arts martiaux ?",
          answer: "L'aïkido se concentre uniquement sur la défense, il n'y a ni compétition, ni coups portés. L'objectif technique est la neutralisation sans blessure. On parle d'ailleurs d'un partenaire plutôt que d'un adversaire."
        },
        {
          question: "L'aïkido est-il efficace pour la self-défense ?",
          answer: "L'aïkido développe des reflexes, la gestion de la distance, la connaissance de son corps, et l'utilisation de la force du partenaire. Il enseigne surtout à éviter et désamorcer les conflits plutôt qu'à \"se battre\"."
        },
        {
          question: "L'aïkido est-il violent ?",
          answer: "Notre philosophie privilégie la non-violence et le respect. Les techniques visent à contrôler et neutraliser sans blesser."
        },
        {
          question: "Comment l'aïkido peut-il m'aider contre le stress ?",
          answer: "L'aïkido offre une coupure mentale dans votre semaine. La concentration sur les techniques et les exercices physiques réduisent naturellement le stress. Le contexte et rituels peu habituels dans notre société (Keikogi, tatami, saluts) aident à se concentrer sur l'instant présent plutôt que sur les tracas du quotidien."
        }
      ]
    },
    {
      title: "ORGANISATION DES COURS",
      items: [
        {
          question: "Quels sont les horaires ?",
          answer: (
            <span>
              Retrouvez les horaires détaillés sur notre{" "}
              <Link to="/#schedule" style={{ color: 'var(--brand-primary)' }}>
                page planning
              </Link>
            </span>
          )
        },
        {
          question: "Où se déroulent les cours ?",
          answer: (
            <span>
              Deux dojos à Cahors selon les horaires. Consultez notre{" "}
              <Link to="/#location" style={{ color: 'var(--brand-primary)' }}>
                section localisation
              </Link>
              {" "}pour les adresses et plans d'accès.
            </span>
          )
        },
        {
          question: "De quel équipement ai-je besoin ?",
          answer: "Pour débuter : tenue de sport souple. Un gi d'aïkido (communément appelé kimono) sera nécessaire après les premiers cours. Le dojo fournit les armes d'entraînement (bokken, jo, tanto). Certains pratiquants achètent leurs propres armes mais ce n'est pas obligatoire."
        },
        {
          question: "Combien coûte la pratique ?",
          answer: (
            <span>
              Consultez notre{" "}
              <Link to="/#pricing" style={{ color: 'var(--brand-primary)' }}>
                section tarifs
              </Link>
              {" "}pour tous les détails sur les cotisations et modalités.
            </span>
          )
        },
        {
          question: "À quelle fréquence dois-je m'entraîner ?",
          answer: "Une fois par semaine minimum pour progresser. La régularité compte plus que la fréquence - mieux vaut 1 fois par semaine constant que 3 fois par semaine de manière irrégulière. Nous sommes néanmoins tous tributaires de la vie et faisons tous comme nous pouvons. Pas de pression !"
        }
      ]
    },
    {
      title: "POUR LES ENFANTS",
      items: [
        {
          question: "À partir de quel âge peut-on commencer ?",
          answer: "Nous accueillons les enfants de 9 à 13 ans. Le programme est spécialement adapté à leur développement : 1h de cours (vs 1h30 adultes) avec techniques adaptées à leur âge."
        },
        {
          question: "L'aïkido rendra-t-il mon enfant agressif ?",
          answer: "Au contraire. L'aïkido enseigne la maîtrise de soi, le respect et la gestion non-violente des conflits. Il développe la confiance sans agressivité et aide particulièrement les enfants ayant des difficultés relationnelles."
        },
        {
          question: "Comment assurez-vous la sécurité des enfants ?",
          answer: "Nous priorisons l'apprentissage des chutes et mouvements de base avant les techniques. Les exercices sont adaptés à l'âge avec des variations sécurisées des techniques adultes."
        },
        {
          question: "Quelles valeurs l'aïkido développe-t-il chez l'enfant ?",
          answer: "Respect, confiance en soi, latéralisation (différencier gauche/droite), coordination, concentration et gestion du stress. L'aïkido peut être particulièrement bénéfique pour les enfants timides ou ayant des difficultés relationnelles."
        }
      ]
    },
    {
      title: "CERTIFICAT MÉDICAL ET SANTÉ",
      items: [
        {
          question: "L'aïkido présente-t-il des risques de blessure ?",
          answer: "Comme dans tous les arts martiaux, il y a un risque de blessure. Néanmoins, l'aïkido a l'un des taux de blessure les plus bas des arts martiaux grâce à sa nature non-compétitive et l'apprentissage prioritaire des chutes (ukemi). Nous dédions toujours une section au début de nos cours à l'échauffement."
        },
        {
          question: "Ai-je besoin d'un certificat médical pour ma première licence ?",
          answer: (
            <div>
              <p className="mb-2"><strong>Adultes :</strong> Oui, obligatoire dans tous les cas.</p>
              <p><strong>Mineurs (moins de 18 ans) :</strong> Non, sauf si le questionnaire de santé révèle un problème. Ils doivent donc commencer par remplir le questionnaire avec leurs parents.</p>
            </div>
          )
        },
        {
          question: "Comment fonctionne le renouvellement de licence ?",
          answer: (
            <div>
              <p className="mb-2">Vous devez remplir un questionnaire de santé d'abord. Si toutes les réponses sont "non", l'attestation que vous avez bien rempli le questionnaire suffit. Si une des réponse est "oui", le certificat médical est obligatoire.</p>
              <p><strong>Pour les adultes :</strong> Le certificat médical est automatiquement requis tous les 3 ans même sans problème de santé.</p>
            </div>
          )
        },
        {
          question: "Qu'est-ce que le questionnaire de santé ?",
          answer: "Un formulaire confidentiel portant sur votre état de santé des 12 derniers mois. Vous ne transmettez au club qu'une attestation confirmant vos réponses, jamais le questionnaire lui-même."
        }
      ]
    },
    {
      title: "FÉDÉRATION FFAAA",
      items: [
        {
          question: "Qu'est-ce que la FFAAA ?",
          answer: "La Fédération Française d'Aïkido, Aïkibudo et Affinitaires est un des organismes officiels qui supervisent la pratique de l'aïkido en France. Reconnue par le Ministère des Sports, elle établit les standards techniques, les grades et la sécurité de la pratique."
        },
        {
          question: "Pourquoi notre dojo est-il affilié à la FFAAA ?",
          answer: "L'affiliation à la FFAAA garantit la qualité et l'authenticité de notre enseignement. Elle nous permet de délivrer des grades officiels reconnus nationalement et internationalement, et assure le respect des protocoles de sécurité établis par la commission médicale fédérale."
        },
        {
          question: "Qu'est-ce que la licence fédérale ?",
          answer: (
            <div>
              <p className="mb-3">La licence fédérale est votre passeport officiel pour pratiquer l'aïkido. Elle inclut :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Assurance responsabilité civile et accidents</strong> couvrant votre pratique</li>
                <li><strong>Accès aux stages fédéraux</strong> et formations avec les experts nationaux</li>
                <li><strong>Possibilité de passage de grades</strong> reconnus par la FFAAA</li>
                <li><strong>Transfert facilité</strong> vers d'autres clubs FFAAA en France</li>
              </ul>
            </div>
          )
        },
        {
          question: "Puis-je pratiquer sans licence fédérale ?",
          answer: "Non. La licence est obligatoire pour des raisons d'assurance et de réglementation sportive française. Aucun cours ne peut être dispensé sans cette couverture légale hors d'événements ponctuels (journées portes ouvertes, séances d'essai, etc.)"
        },
        {
          question: "La licence est-elle valable dans d'autres clubs ?",
          answer: "Oui, votre licence FFAAA est reconnue dans tous les dojos affiliés en France. En cas de déménagement ou de voyage, vous êtes couverts pour pratiquer dans n'importe quel club FFAAA."
        },
        {
          question: "Quand dois-je renouveler ma licence ?",
          answer: "Chaque année, généralement en septembre lors de la rentrée sportive. Le renouvellement suit les mêmes règles que la première demande (certificat médical ou questionnaire de santé selon votre situation)."
        }
      ]
    },
    {
      title: "TENUE ET ÉTIQUETTE",
      items: [
        {
          question: "Que dois-je porter pour mon premier cours ?",
          answer: "Venez en tenue de sport souple : jogging, t-shirt, pas de chaussures sur les tatamis. Évitez les vêtements avec fermetures éclair ou boutons qui pourraient blesser lors des contacts."
        },
        {
          question: "Quand dois-je acheter un gi (kimono) ?",
          answer: "Après quelques cours d'essai, un gi d'aïkido, judo ou karaté blanc devient obligatoire."
        },
        {
          question: "Qu'est-ce que le hakama et qui le porte ?",
          answer: "Le hakama est la jupe-pantalon traditionnelle noire ou bleu marine portée par-dessus le gi. Dans notre dojo, il se porte généralement à partir du 3ème kyu."
        },
        {
          question: "Y a-t-il des règles particulières pour la tenue ?",
          answer: "Le gi doit être propre et en bon état. Pas de bijoux, montres ou objets qui pourraient blesser. Les cheveux longs doivent être attachés. La propreté corporelle est essentielle par respect pour les partenaires."
        },
        {
          question: "Comment dois-je saluer dans le dojo ?",
          answer: (
            <div>
              <p className="mb-2"><strong>En entrant/sortant :</strong> Saluez debout face aux kamiza et vers le tatami.</p>
              <p className="mb-2"><strong>Avant/après cours :</strong> Salut assis (seiza) collectif dirigé par le professeur.</p>
              <p className="mb-2"><strong>Avec un partenaire :</strong> Salut debout au début et à la fin de chaque exercice.</p>
              <p><strong>Vers le professeur :</strong> Saluez quand il vous donne une explication personnelle.</p>
            </div>
          )
        },
        {
          question: "Que signifient ces saluts ?",
          answer: "Le salut (rei) exprime le respect mutuel et la gratitude. Il marque la transition entre l'espace quotidien et l'espace d'apprentissage. C'est une reconnaissance de votre partenaire et de la transmission reçue."
        },
        {
          question: "Puis-je boire ou manger sur les tatamis ?",
          answer: "Non. Les tatamis sont un espace dédié à la pratique. Boire et manger se font en dehors de l'espace de pratique."
        },
        {
          question: "Comment dois-je me comporter pendant les cours ?",
          answer: "Écoutez attentivement les explications, ne parlez pas pendant les démonstrations du professeur, restez concentré. Si vous arrivez en retard, attendez un signe du professeur avant de vous joindre au cours."
        }
      ]
    },
    {
      title: "LE KAMIZA",
      items: [
        {
          question: "Qu'est-ce que le kamiza ?",
          answer: "Le kamiza est la \"place d'honneur\" du dojo, littéralement le \"siège supérieur\". C'est l'endroit vers lequel nous nous tournons pour saluer au début et à la fin des cours. Il représente le respect pour la tradition et l'esprit de l'aïkido."
        },
        {
          question: "Que trouve-t-on dans votre kamiza ?",
          answer: "Notre kamiza est composé d'une branche de sakura (cerisier japonais) accompagnée du kanji \"aïkido\"."
        },
        {
          question: "Que représente le kanji aïkido ?",
          answer: "Le kanji affiché rappelle les trois principes fondamentaux : Ai (harmonie), Ki (énergie vitale), Do (voie)."
        },
        {
          question: "Pourquoi saluer vers le kamiza ?",
          answer: "Ce salut n'est pas religieux mais marque notre respect pour la tradition transmise, pour l'espace de pratique et pour l'engagement que nous prenons en entrant sur les tatamis. C'est un moment de recentrage."
        },
        {
          question: "Dois-je avoir des croyances particulières ?",
          answer: "Non. Le respect du kamiza fait partie de l'étiquette martiale, comme se serrer la main dans d'autres sports. Il s'agit de courtoisie et de reconnaissance de la tradition, pas de spiritualité imposée."
        },
        {
          question: "Puis-je m'asseoir dos au kamiza ?",
          answer: "Par respect, on évite de tourner le dos au kamiza quand c'est possible. Si l'organisation du cours l'impose, ce n'est pas grave - l'intention respectueuse compte plus que la position exacte."
        }
      ]
    },
    {
      title: "LES ARMES",
      items: [
        {
          question: "Quelles armes utilise-t-on en aïkido ?",
          answer: (
            <div>
	      <p className="mb-3">Nous pratiquons avec trois armes traditionnelles en bois :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
		<li><strong>Bokken :</strong> sabre en bois (environ 100cm)</li>
		<li><strong>Jo :</strong> bâton droit (environ 128cm)</li>
		<li><strong>Tanto :</strong> couteau en bois (environ 30cm)</li>
              </ul>
            </div>
	  )
        },
        {
          question: "Pourquoi des armes en aïkido ?",
          answer: "Les armes développent la précision, la distance, le timing et renforcent la compréhension des mouvements à mains nues. Beaucoup de techniques d'aïkido dérivent des mouvements du sabre."
        },
        {
          question: "Dois-je acheter mes propres armes ?",
          answer: "Non, le dojo fournit bokken, jo et tanto pendant les cours. Après quelques mois de pratique, vous pourrez choisir d'investir dans vos propres armes (comptez 40-80€ l'ensemble) pour un meilleur confort et une pratique personnelle."
        },
        {
          question: "Les armes sont-elles dangereuses ?",
          answer: "Bien qu'en bois, elles restent des armes et demandent respect et attention. Nous les manipulons comme de vraies armes : jamais pointées vers quelqu'un, rangées avec soin, utilisées uniquement selon les consignes du professeur."
        },
      ]
    }
  ];

  return (
    <section className="section-padding section-dark">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold uppercase mb-6" style={{ color: 'var(--brand-primary)', hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Guide Complet - Questions Fréquentes</h2>
          <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: 'var(--text-on-dark)' }}>
            Tout ce que vous devez savoir sur l'aïkido, notre dojo et les démarches administratives. 
            Cliquez sur les catégories pour explorer les réponses détaillées.
          </p>
        </div>

        {/* FAQ Categories Accordion */}
        <div className="max-w-5xl mx-auto space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <div
              key={categoryIndex}
              className="card-dark border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg"
              style={{ borderColor: 'var(--border-medium)' }}
            >
              {/* Category Header - Clickable */}
              <button
                onClick={() => toggleCategory(categoryIndex)}
                className="w-full px-8 py-6 text-left flex items-center justify-between gap-4 transition-all duration-300 hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{ 
                  backgroundColor: openCategories.has(categoryIndex) ? 'var(--bg-card-dark)' : 'transparent'
                }}
                aria-expanded={openCategories.has(categoryIndex)}
              >
                <div className="flex items-center gap-4">
                  <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide" style={{ color: 'var(--text-on-dark)', hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                    {category.title}
                  </h3>
                </div>
                <div className="flex-shrink-0 transition-transform duration-300"
                     style={{ 
                       transform: openCategories.has(categoryIndex) ? 'rotate(180deg)' : 'rotate(0deg)',
                       color: 'var(--brand-primary)'
                     }}>
                  <ChevronDown size={24} />
                </div>
              </button>

              {/* Category Content - Expandable */}
              <div
                className="overflow-hidden transition-all duration-500 ease-in-out"
                style={{
                  maxHeight: openCategories.has(categoryIndex) ? '2000px' : '0px',
                  opacity: openCategories.has(categoryIndex) ? 1 : 0
                }}
              >
                <div className="px-8 pb-6">
                  <div className="border-t pt-8 space-y-6" style={{ borderColor: 'var(--border-medium)' }}>
                    {category.items.map((item, itemIndex) => {
                      const itemKey = `${categoryIndex}-${itemIndex}`;
                      const anchor = generateAnchor(categoryIndex, itemIndex, item.question);
                      return (
                        <div
                          key={itemIndex}
                          id={anchor}
                          className="border rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md group"
                          style={{ borderColor: 'var(--border-light)' }}
                        >
                          {/* Question - Clickable */}
                          <div 
                            data-question-header
                            className="px-6 py-5 flex items-center justify-between gap-4 transition-all duration-300"
                            style={{ 
                              backgroundColor: openItems.has(itemKey) ? 'var(--bg-card-dark)' : 'transparent'
                            }}
                          >
                            {/* Question - Clickable */}
                            <button
                              onClick={() => toggleItem(itemKey)}
                              className="flex-1 text-left transition-all duration-300 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-opacity-50 rounded p-1 -m-1"
                              style={{ 
                                focusRingColor: 'var(--brand-primary)'
                              }}
                              aria-expanded={openItems.has(itemKey)}
                            >
                              <h4 className="text-sm sm:text-base font-medium" style={{ color: 'var(--brand-primary)' }}>
                                {item.question}
                              </h4>
                            </button>
                            
                            {/* Copy Link Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyLink(anchor);
                              }}
                              className="opacity-0 group-hover:opacity-70 hover:opacity-100 transition-opacity duration-200 p-2 rounded focus:outline-none focus:ring-1"
                              style={{ 
                                color: 'var(--brand-primary)',
                                focusRingColor: 'var(--brand-primary)'
                              }}
                              title="Copier le lien vers cette question"
                              aria-label="Copier le lien vers cette question"
                            >
                              {copiedId === anchor ? (
                                <Check size={16} />
                              ) : (
                                <Link2 size={16} />
                              )}
                            </button>
                            
                            {/* Expand/Collapse Chevron */}
                            <button
                              onClick={() => toggleItem(itemKey)}
                              className="flex-shrink-0 transition-all duration-300 p-1 rounded focus:outline-none focus:ring-1"
                              style={{ 
                                transform: openItems.has(itemKey) ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: 'var(--brand-primary)',
                                opacity: 0.8,
                                focusRingColor: 'var(--brand-primary)'
                              }}
                              aria-expanded={openItems.has(itemKey)}
                              aria-label={openItems.has(itemKey) ? 'Fermer la réponse' : 'Ouvrir la réponse'}
                            >
                              <ChevronDown size={18} />
                            </button>
                          </div>

                          {/* Answer - Expandable */}
                          <div
                            className="overflow-hidden transition-all duration-300 ease-in-out"
                            style={{
                              maxHeight: openItems.has(itemKey) ? '500px' : '0px',
                              opacity: openItems.has(itemKey) ? 1 : 0
                            }}
                          >
                            <div className="px-6 pb-6">
                              <div className="border-t pt-5" style={{ borderColor: 'var(--border-light)' }}>
                                <div className="text-sm sm:text-base leading-relaxed opacity-90" style={{ color: 'var(--text-on-dark)' }}>
                                  {typeof item.answer === 'string' ? item.answer : item.answer}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Documents Section */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="card-dark p-8 rounded-xl border-2 shadow-lg" style={{ borderColor: 'var(--brand-primary)' }}>
            <h3 className="text-xl sm:text-2xl font-bold mb-6 text-center uppercase tracking-wide" style={{ color: 'var(--brand-primary)', hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>
              Documents Médicaux
            </h3>
            <p className="text-sm sm:text-base mb-6 text-center" style={{ color: 'var(--text-on-dark)' }}>
              Voici tous les documents nécessaires pour faciliter vos démarches médicales :
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-lg font-semibold mb-3" style={{ color: 'var(--brand-primary)' }}>
                  Questionnaires de Santé
                </h4>
                <div className="space-y-3">
                  <div>
                    <PDFLink fileName="questionnaire-sante-adultes.pdf">
                      Questionnaire médical (Adultes)
                    </PDFLink>
                  </div>
                  <div>
                    <PDFLink fileName="questionnaire-sante-enfants.pdf">
                      Questionnaire médical (Mineurs)
                    </PDFLink>
                  </div>
                  <div>
                    <PDFLink fileName="attestation-sante.pdf">
                      Attestation de Santé
                    </PDFLink>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="text-lg font-semibold mb-3" style={{ color: 'var(--brand-primary)' }}>
                  Informations Officielles
                </h4>
                <div className="space-y-3">
                  <div>
                    <PDFLink fileName="Note-dinfo_certif_med-du-25-07-2022.pdf">
                      Note d'information FFAAA
                    </PDFLink>
                    <p className="text-xs sm:text-sm mt-1" style={{ color: 'var(--text-secondary-dark)' }}>
                      Explications officielles des obligations médicales
                    </p>
                  </div>
                  <div>
                    <PDFLink fileName="certificat-medical-exemple.pdf">
                      Exemple de certificat médical
                    </PDFLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call-to-Action */}
	{/*<div className="text-center mt-20">
          <p className="text-sm sm:text-base mb-6" style={{ color: 'var(--text-on-dark)' }}>
            Une question qui n'est pas dans cette liste ? N'hésitez pas à nous contacter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#location" className="btn-primary">
              Nous Rendre Visite
            </Link>
            <Link to="/#pricing" className="btn-secondary">
              Réserver un Essai
            </Link>
          </div>
        </div>*/}
      </div>
    </section>
  );
};

export default ComprehensiveFAQ;
